import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { OpportunityKind } from 'anchor-scout-common/types';
import { timeModel } from '~/models';
import { currencyUtils } from '~/utils/currency.utils';
import { classifyConfidence } from './classifyConfidence';
import { formatYearlyAmount } from './formatOpportunityValues';
import { confidenceLabel, kindText } from './opportunityLabels';
const AVERAGE_DAYS_PER_MONTH = 30;
const COUNT_UP_START_SHARE = 0.6;
const KNOWN_KINDS = Object.values(OpportunityKind);
export const matchCard = (card, visitor)=>card.type === 'single' ? visitor.single(card) : visitor.group(card);
const cardKind = (card)=>matchCard(card, {
        single: (param)=>{
            let { opportunity } = param;
            return opportunity.kind;
        },
        group: (param)=>{
            let { kind } = param;
            return kind;
        }
    });
export const cardIdentity = (card)=>matchCard(card, {
        single: (param)=>{
            let { opportunity } = param;
            return opportunity.opportunityKey;
        },
        group: (param)=>{
            let { kind, serviceKey } = param;
            return "".concat(kind, ":").concat(serviceKey);
        }
    });
export const leadOpportunity = (card)=>matchCard(card, {
        single: (param)=>{
            let { opportunity } = param;
            return opportunity;
        },
        group: (param)=>{
            let { opportunities } = param;
            return opportunities[0];
        }
    });
export const partitionByRecognisedKind = (cards)=>({
        recognised: cards.filter((card)=>KNOWN_KINDS.includes(cardKind(card))),
        unrecognised: cards.filter((card)=>!KNOWN_KINDS.includes(cardKind(card)))
    });
export const unrecognisedKindReport = (skipped)=>skipped.length === 0 ? null : "revenue-opportunities: skipped ".concat(skipped.length, " cards of unrecognised kind: ").concat(Array.from(new Set(skipped.map(cardKind))).sort().join(', '));
const singleCardCopy = (opportunity, texts)=>{
    var _opportunity_clientName;
    const copy = texts.section.card;
    const client = (_opportunity_clientName = opportunity.clientName) !== null && _opportunity_clientName !== void 0 ? _opportunity_clientName : texts.unknownClient();
    const service = opportunity.subject;
    switch(opportunity.kind){
        case OpportunityKind.StalePrice:
            {
                const { monthsSinceIncrease, billedInvoiceCount, currentRate } = opportunity.evidence;
                const rate = currencyUtils.formatDollarAmount(currentRate);
                return {
                    lead: copy.stalePrice.lead({
                        client
                    }),
                    description: monthsSinceIncrease == null ? copy.stalePrice.neverRaised({
                        service,
                        count: billedInvoiceCount,
                        rate
                    }) : copy.stalePrice.lastRaised({
                        service,
                        months: monthsSinceIncrease,
                        count: billedInvoiceCount,
                        rate
                    }),
                    rowTitle: copy.stalePrice.row({
                        service,
                        client
                    })
                };
            }
        case OpportunityKind.DiscountLeak:
            {
                const { currentDiscountRate, firstDiscountedAt, discountedInvoiceCount } = opportunity.evidence;
                return {
                    lead: copy.discountLeak.lead({
                        client
                    }),
                    description: copy.discountLeak.description({
                        rate: currencyUtils.formatPercent(currentDiscountRate),
                        service,
                        date: timeModel.formatMonthYear(firstDiscountedAt),
                        count: discountedInvoiceCount
                    }),
                    rowTitle: copy.discountLeak.row({
                        service,
                        client
                    })
                };
            }
        case OpportunityKind.RecurringAdhoc:
            {
                const { occurrenceCount, cadenceEstimateDays } = opportunity.evidence;
                return {
                    lead: copy.recurringAdhoc.lead({
                        client
                    }),
                    description: cadenceEstimateDays == null ? copy.recurringAdhoc.withoutCadence({
                        service,
                        count: occurrenceCount
                    }) : copy.recurringAdhoc.withCadence({
                        service,
                        count: occurrenceCount,
                        months: Math.max(1, Math.round(cadenceEstimateDays / AVERAGE_DAYS_PER_MONTH))
                    }),
                    rowTitle: copy.recurringAdhoc.row({
                        client
                    })
                };
            }
        case OpportunityKind.CrossSell:
            return {
                lead: copy.crossSell.lead({
                    service,
                    client
                }),
                description: copy.crossSell.description({
                    rate: currencyUtils.formatPercent(opportunity.evidence.peerAdoptionRate)
                }),
                rowTitle: copy.crossSell.row({
                    service,
                    client
                })
            };
    }
};
const groupCardCopy = (card, texts)=>{
    const copy = texts.section.card;
    const service = card.subject;
    const count = card.clientCount;
    const lead = card.opportunities[0];
    return lead.kind === OpportunityKind.CrossSell ? {
        lead: copy.crossSell.groupLead({
            service,
            count
        }),
        description: copy.crossSell.description({
            rate: currencyUtils.formatPercent(lead.evidence.peerAdoptionRate)
        }),
        rowTitle: copy.crossSell.groupRow({
            service,
            count
        })
    } : {
        lead: copy.stalePrice.groupLead({
            count
        }),
        description: copy.stalePrice.groupDescription({
            count,
            service
        }),
        rowTitle: copy.stalePrice.groupRow({
            service,
            count
        })
    };
};
export const describeCard = (card, texts)=>{
    const opportunity = leadOpportunity(card);
    const confidenceBand = classifyConfidence(opportunity.confidence);
    const { amount, copy, actionLabel } = matchCard(card, {
        single: (single)=>({
                amount: single.opportunity.estAnnualImpact,
                copy: singleCardCopy(single.opportunity, texts),
                actionLabel: kindText(opportunity.kind, texts.action)
            }),
        group: (group)=>({
                amount: group.estAnnualImpact,
                copy: groupCardCopy(group, texts),
                actionLabel: null
            })
    });
    const sign = opportunity.kind === OpportunityKind.RecurringAdhoc ? '' : '+';
    return _object_spread_props(_object_spread({
        identity: cardIdentity(card),
        kind: opportunity.kind,
        typeLabel: kindText(opportunity.kind, texts.kind),
        confidenceBand,
        confidenceLabel: confidenceLabel(confidenceBand, texts.confidence),
        amount: "".concat(sign).concat(formatYearlyAmount(amount)),
        amountFrom: "".concat(sign).concat(formatYearlyAmount(amount * COUNT_UP_START_SHARE)),
        rowAmount: formatYearlyAmount(amount)
    }, copy), {
        actionLabel
    });
};
export const opportunityBiData = (view, position)=>({
        kind: view.kind,
        position
    });
export const resolveFrontIndex = (identities, front)=>{
    const identityIndex = front.identity == null ? -1 : identities.indexOf(front.identity);
    return identityIndex >= 0 ? identityIndex : Math.max(0, Math.min(front.index, identities.length - 1));
};
